/**
 * Dashboard Layout Component
 *
 * NAVIGATION ARCHITECTURE:
 * This layout implements a dual navigation system optimized for different viewport sizes:
 *
 * 1. Desktop (≥1280px): Top navigation bar
 *    - Fixed header with logo, nav links, Empathy button, and the profile block
 *    - Rendered only when `isDesktop` (a px query, the one gate: no rem `xl:` class)
 *    - Uses `data-testid="desktop-nav-*"` / `desktop-profile-link` for testing
 *    - The Empy and support launchers float at the bottom right
 *
 * 2. Mobile/Tablet (<1280px): compact header + bottom navigation bar
 *    - Compact header IN THE FLOW (logo, docked Empy and support launchers,
 *      profile avatar `data-testid="mobile-profile-link"`), so page content
 *      never runs under any of them (redesign PR3, 2026-10-20)
 *    - Fixed footer with icon-based navigation and the raised E
 *    - Rendered only when `!isDesktop`
 *    - Uses `data-testid="mobile-nav-*"` for testing
 *
 * The look lives in design-system/shell.css (`.ee-shell` scope); pages that
 * have not been reskinned keep their own body look between the bars.
 *
 * Both navigations share the same routes but have different UX patterns appropriate
 * for their respective platforms. The breakpoint (1280px) matches Tailwind's `xl`
 * breakpoint and is defined in hooks/useMediaQuery.ts as BREAKPOINTS.xl.
 *
 * Test helpers in e2e/fixtures.js use data-testid attributes to reliably select
 * the correct navigation element based on viewport size.
 */

import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { useEffect, useState } from 'react';
import type { JSX } from 'react/jsx-runtime';
import { LuBookOpen, LuChevronRight, LuGamepad2, LuTrendingUp, LuWrench } from 'react-icons/lu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import GuestBanner from '../../components/auth/GuestBanner';
import VerifyEmailBanner from '../../components/auth/VerifyEmailBanner';
import EmpyFloatingButton from '../../components/EmpyFloatingButton';
import { CALL_ROUTE } from '../../components/empathy/callRoutes';
import QueueOverlay from '../../components/empathy/QueueOverlay';
import { useFullScreenStageOpen } from '../../components/stage/fullScreenStage';
import SupportChatButton from '../../components/support-chat/SupportChatButton';
import BrandLogo from '../../components/theme/BrandLogo';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { getEcho, isWsEnabled } from '../../services/echo';
import { safeAreaService } from '../../services/safearea.service';
import { useAuthStore } from '../../stores/auth.store';
import { isCallSessionLive, useEmpathyQueueStore } from '../../stores/empathy-queue.store';
import { useOnboardingStore } from '../../stores/onboarding.store';
import { useProgressStore } from '../../stores/progress.store';
import { beginSessionFlow } from '../../stores/session-read';
import { useSubscriptionStore } from '../../stores/subscription.store';
import type { QuickTourSection } from '../../types/onboarding';
import { fireAndForget } from '../../utils/fireAndForget';

type NavIcon = 'course' | 'progress' | 'games' | 'tools';

interface LinkButtonProps {
  to: string;
  icon: NavIcon;
  label: string;
  testId?: string;
  dataOnboarding?: string;
}

/**
 * Generate a test ID from route path
 * e.g., "/app" -> "nav-course", "/app/progress" -> "nav-progress"
 */
function getNavTestId(to: string, prefix: string = 'mobile'): string {
  // Remove /app prefix for test ID generation
  const route = to === '/app' ? 'course' : to.replace('/app/', '');
  return `${prefix}-nav-${route}`;
}

// Lucide, one stroke family for the whole shell (branding direction, section 4).
const ICON_MAP: Record<NavIcon, JSX.Element> = {
  course: <LuBookOpen aria-hidden="true" />,
  progress: <LuTrendingUp aria-hidden="true" />,
  games: <LuGamepad2 aria-hidden="true" />,
  tools: <LuWrench aria-hidden="true" />,
};

/**
 * One nav item's route and tour state, shared by the bottom nav and the top
 * bar so the two cannot disagree about which item is active or how a tap
 * behaves during the quick tour's intro.
 */
function useNavItem(to: string, dataOnboarding?: string) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const handleTourNavClick = useOnboardingStore((s) => s.handleTourNavClick);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const quickTourPhase = useOnboardingStore((s) => s.quickTourPhase);
  const getCurrentQuickTourSection = useOnboardingStore((s) => s.getCurrentQuickTourSection);
  const isActive = pathname === to || (pathname.indexOf(to) === 0 && to !== '/app');

  const isTourIntro = currentStep === 'quick-tour' && quickTourPhase === 'intro';
  const section = dataOnboarding ? (dataOnboarding.replace('nav-', '') as QuickTourSection) : null;
  const isCurrentTourTarget = isTourIntro && section === getCurrentQuickTourSection();

  const handleClick = (e: React.MouseEvent) => {
    if (isTourIntro && section) {
      e.preventDefault(); // Always prevent Link's default during tour intro
      if (isCurrentTourTarget) {
        handleTourNavClick(section);
        void navigate(to); // Explicitly navigate to target page
      }
      return;
    }
  };

  const tourClass = `${isCurrentTourTarget ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''} ${isTourIntro && !isCurrentTourTarget ? 'opacity-30' : ''}`;

  return { isActive, handleClick, tourClass };
}

function LinkButton({ to, icon, label, testId, dataOnboarding }: LinkButtonProps) {
  const { isActive, handleClick, tourClass } = useNavItem(to, dataOnboarding);

  return (
    <Link
      to={to}
      onClick={handleClick}
      data-testid={testId || getNavTestId(to, 'mobile')}
      data-onboarding={dataOnboarding}
      aria-current={isActive ? 'page' : undefined}
      className={`ee-bnav__link ${isActive ? 'ee-bnav__link--active' : ''} ${tourClass}`}
    >
      {ICON_MAP[icon]}
      <span>{label}</span>
    </Link>
  );
}

// Desktop navigation link component
function DesktopLinkButton({ to, icon, label, testId, dataOnboarding }: LinkButtonProps) {
  const { isActive, handleClick, tourClass } = useNavItem(to, dataOnboarding);

  return (
    <Link
      to={to}
      onClick={handleClick}
      data-testid={testId || getNavTestId(to, 'desktop')}
      data-onboarding={dataOnboarding}
      aria-current={isActive ? 'page' : undefined}
      className={`ee-topbar__link ${isActive ? 'ee-topbar__link--active' : ''} ${tourClass}`}
    >
      {ICON_MAP[icon]}
      <span>{label}</span>
    </Link>
  );
}

/** The profile picture, with the gold ring a paid plan has always had. */
function ProfileAvatar({ size, alt, paid }: { size: number; alt: string; paid: boolean }) {
  return (
    <span
      className={`ee-avatar ${paid ? 'ee-avatar--paid' : ''}`}
      style={{ width: size, height: size }}
    >
      <img src="/default_profile_image.webp" alt={alt} />
    </span>
  );
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  background?: string;
  /**
   * The giraffe coat under the page in dark (docs/plans/DARK-MODE.md §7a):
   * on for the calm hubs (Empathy home, Profile, Progress, Tools, Games),
   * off for reading, focus and live pages. Nothing in light.
   */
  coat?: boolean;
}

interface SafeAreaValues {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

function DashboardLayout({ children, background = '', coat = false }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [safeAreaValues, setSafeAreaValues] = useState<SafeAreaValues>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });
  const [keyboardVisible, setKeyboardVisible] = useState<boolean>(false);
  const [isLeavingQueue, setIsLeavingQueue] = useState<boolean>(false);
  const isDesktop = useIsDesktop();
  const user = useAuthStore((state) => state.user);
  const isGuest = useAuthStore((state) => state.isGuest);
  const isPaidPlan = useSubscriptionStore((state) => state.isPaidPlan());

  // Onboarding tour state — elevate nav above overlay during quick-tour intro
  const isTourIntro = useOnboardingStore(
    (s) => s.currentStep === 'quick-tour' && s.quickTourPhase === 'intro',
  );
  const tourSection = useOnboardingStore((s) => s.getCurrentQuickTourSection());
  const navZClass = isTourIntro ? 'z-[201]' : 'z-(--ee-z-chrome)';

  /**
   * A FULL-SCREEN STAGE OWNS THE SCREEN, SO THE CHROME STANDS DOWN.
   *
   * The 1:1 call stage is a `fixed inset-0 z-30` overlay rendered from inside
   * this layout's page. Fixed took it out of the page's FLOW; it did not take
   * it out of the page's CHROME, and the bottom nav below is `z-50`. On a real
   * phone that nav sat on top of the call's control row and took its taps:
   * End Call navigated to the Course page and left the call running, mic hit
   * Tools, camera hit Games. Raising the stage instead is not available — it
   * is deliberately below the `z-40` queue pill, and the nav (50), the raised
   * E button (99) and these floating buttons (100) are all above that.
   *
   * NOTHING FLOATS OVER A STAGE any more, the queue pill included. It used to
   * be the exception — it was the other way back to the queue — but on a phone
   * it sat on the call's own words, and the stage already carries everything
   * it offered: End in the control row, the state and the clock in the top
   * bar. `QueueOverlay` returns null for as long as this flag is up; the
   * locked modal is the one thing left that may cover a stage.
   */
  const fullScreenStageOpen = useFullScreenStageOpen();
  const fetchProgress = useProgressStore((s) => s.fetchData);
  const progress = useProgressStore((s) => s.progress);
  const isProgressLoading = useProgressStore((s) => s.isLoading);

  const {
    giverState,
    seekerState,
    queuePosition,
    estimatedWaitMinutes,
    reliabilityScore,
    queueStats,
    leaveGiverQueue,
    cancelEmpathyRequest,
    requestEndSession,
    fetchGiverStatus,
    fetchSeekerStatus,
  } = useEmpathyQueueStore();

  // Determine if user is currently in an empathy queue or session
  // Used to show visual indicator on the E button

  const handleLeaveQueue = async (): Promise<void> => {
    // Prevent multiple clicks while request is in-flight
    if (isLeavingQueue) return;

    /**
     * LEAVING THE QUEUE IS NOT ENDING A CALL, and this is the guard for the
     * gap between the two. The pill reaches this from its cooldown panel
     * (since A7, 2026-10-18, a WAITING pill's tap goes back to the waiting
     * screen, whose own Leave runs the page's leave instead); its in-call
     * state has its own "End call", which sends the one
     * end-session signal the stage's control row sends — but the state can
     * flip to ACTIVE between the render and the tap. A live session is never
     * torn down by the queue-leave path: it goes to the same signal, so the
     * flow can run the teardown AND the feedback prompt (class C).
     *
     * THE SAME PREDICATE THE PILL RENDERS FROM, read WHOLLY off the store at
     * the moment of the tap — which is what the paragraph above is about: the
     * state can flip to ACTIVE between the render that drew this button and
     * the thumb that hits it, so the render's copy is the wrong one to decide
     * on (class A). This guard and the pill's `in-call` state are one question
     * — "is there a live call here?" — and two transcriptions of it is how the
     * pill stops offering End while this branch goes on taking it (class D).
     * It no longer also demands a `currentSessionId`: a state that says ACTIVE
     * with no session id is exactly the moment this guard exists for, and the
     * end-session signal is the safe branch either way.
     */
    if (isCallSessionLive(useEmpathyQueueStore.getState())) {
      requestEndSession();
      // Navigate to the empathy page so the Flow component is mounted and can
      // handle the signal.
      if (location.pathname !== '/app/empathy') {
        void navigate(CALL_ROUTE);
      }
      return;
    }

    setIsLeavingQueue(true);
    try {
      // One sequence, one account (#940): the second leave must not go out
      // for whoever signed in while the first was out.
      const flow = beginSessionFlow();
      if (giverState !== 'OFFLINE') {
        await leaveGiverQueue();
      }
      if (seekerState && flow.isCurrent()) {
        await cancelEmpathyRequest();
      }
    } catch (error) {
      console.error('Error leaving queue:', error);
    } finally {
      setIsLeavingQueue(false);
    }
  };

  useEffect(() => {
    // Guests included: the progress store serves them the anonymous fresh-user
    // shape from the public preview endpoint (no authed call).
    if (!progress && !isProgressLoading) {
      void fetchProgress();
    }
  }, [progress, isProgressLoading, fetchProgress]);

  // Re-fetch progress silently on route changes so empathy lock status stays fresh.
  // The layout never unmounts during SPA navigation, so without this the progress
  // data from initial load would go stale (e.g., empathy still showing locked after
  // the user completes the intro domain on another page).
  useEffect(() => {
    if (progress) {
      void fetchProgress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── Global queue status: WS subscription + slow fallback poll ──
  // Runs at the layout level so notifications arrive regardless of which page the user is on.
  const isInQueue = giverState === 'WAITING' || giverState === 'PINGED' || giverState === 'ACTIVE';
  const isSeeker = seekerState === 'MATCHING' || seekerState === 'CONNECTED';

  useEffect(() => {
    if (!user?.id || (!isInQueue && !isSeeker)) return;

    // WS: subscribe to empathy status events for instant notification.
    // See GiverFlow.tsx V5 comment — stopListening must pass the handler
    // reference, otherwise it removes every component's listener on the
    // channel (4 components share `user.{id}.empathy.status.changed`).
    const handler = (event: { role: string }) => {
      if (event.role === 'giver') void fetchGiverStatus();
      if (event.role === 'seeker') void fetchSeekerStatus();
    };
    const wsActive = isWsEnabled();
    if (wsActive) {
      const echo = getEcho();
      const channelName = `user.${user.id}`;
      echo.private(channelName).listen('.empathy.status.changed', handler);
    }

    // Slow fallback poll (15s) — catches missed WS events or when WS is disabled
    const interval = setInterval(() => {
      if (isInQueue) void fetchGiverStatus();
      if (isSeeker) void fetchSeekerStatus();
    }, 15000);

    return () => {
      clearInterval(interval);
      if (wsActive && user?.id) {
        try {
          getEcho().private(`user.${user.id}`).stopListening('.empathy.status.changed', handler);
        } catch {
          /* echo may not be initialized */
        }
      }
    };
  }, [user?.id, isInQueue, isSeeker, fetchGiverStatus, fetchSeekerStatus]);

  useEffect(() => {
    let mounted = true;
    if (mounted) setSafeAreaValues(safeAreaService.getSafeAreaValues());
    const handleViewportChange = (): void => {
      setTimeout(() => {
        if (!mounted) return;
        setSafeAreaValues(safeAreaService.getSafeAreaValues());
      }, 200);
    };

    window.addEventListener('orientationchange', handleViewportChange);
    window.addEventListener('resize', handleViewportChange);

    // Listen for keyboard events
    if (Capacitor.isNativePlatform()) {
      void Keyboard.addListener('keyboardWillShow', () => setKeyboardVisible(true));
      void Keyboard.addListener('keyboardWillHide', () => setKeyboardVisible(false));
    }

    return () => {
      mounted = false;
      window.removeEventListener('orientationchange', handleViewportChange);
      window.removeEventListener('resize', handleViewportChange);
      if (Capacitor.isNativePlatform()) void Keyboard.removeAllListeners();
    };
  }, []);

  const isEmpathyActive = location.pathname.startsWith('/app/empathy');

  // The Empathy entries (top bar item, raised E, its label) share one path.
  const goToEmpathy = (): void => {
    // Handle empathy tour: advance from step 0 (E button click) to step 1
    const { empathyTourActive, empathyTourIndex, advanceEmpathyTour } =
      useOnboardingStore.getState();
    if (empathyTourActive && empathyTourIndex === 0) {
      advanceEmpathyTour();
      void navigate('/app/empathy');
      return;
    }
    // Suppress during onboarding tour intro
    const { currentStep, quickTourPhase } = useOnboardingStore.getState();
    if (currentStep === 'quick-tour' && quickTourPhase === 'intro') return;
    void navigate('/app/empathy');
  };

  // The profile entries (top bar block, compact header avatar) share one path.
  const handleProfileClick = (e: React.MouseEvent): void => {
    const { currentStep, quickTourPhase, getCurrentQuickTourSection, handleTourNavClick } =
      useOnboardingStore.getState();
    if (currentStep === 'quick-tour' && quickTourPhase === 'intro') {
      e.preventDefault(); // Always prevent Link's default during tour intro
      if (getCurrentQuickTourSection() === 'profile') {
        handleTourNavClick('profile');
        void navigate('/app/profile');
      }
    }
  };

  const isProfileTourTarget = isTourIntro && tourSection === 'profile';
  const profileTourStyle = isProfileTourTarget
    ? {
        boxShadow:
          '0 0 0 4px var(--ee-surface), 0 0 0 8px var(--color-primary), 0 0 24px 4px var(--color-primary)',
      }
    : undefined;
  const brandTo = Capacitor.isNativePlatform() ? '/app' : '/';
  const firstName = user?.name?.split(' ')[0] || 'Profile';

  return (
    <div
      className={`ee-shell-root min-h-screen safe-area-horizontal${coat ? ' ee-coat' : ''}`}
      style={{
        paddingLeft: `${safeAreaValues.left}px`,
        paddingRight: `${safeAreaValues.right}px`,
      }}
    >
      {/* Desktop Navigation - Top bar for large desktop screens (1280px+).
          Rendered only there, so each tour hook (`data-onboarding`) exists
          once on the page: the tour's querySelector takes the first match,
          and a hidden copy of the bar ahead of the visible one used to win.
          `isDesktop` (a px query) is the ONLY gate: no `hidden xl:flex`
          backstop and no `xl:` class on <main>. Tailwind's breakpoints are
          rem, so with a 20px default font at 1400 wide `xl` (80rem = 1600px)
          missed while isDesktop held, and the bar was mounted but hidden
          with the phone bars unmounted: no navigation at all (#1022 review). */}
      {isDesktop && !fullScreenStageOpen ? (
        <nav
          data-notice-anchor=""
          className={`ee-shell ee-topbar flex fixed top-0 left-0 right-0 h-(--ee-topbar-h) ${navZClass} items-center`}
        >
          <div className="flex items-center justify-between gap-6 w-full max-w-6xl mx-auto px-6">
            {/* Logo */}
            <Link to={brandTo} className="ee-brand">
              <BrandLogo />
              <span className="ee-brand__word">EmpathEase</span>
            </Link>

            {/* Navigation Links */}
            <div className="ee-topbar__nav">
              <DesktopLinkButton
                to="/app"
                icon="course"
                label="Course"
                dataOnboarding="nav-course"
              />
              <DesktopLinkButton
                to="/app/progress"
                icon="progress"
                label="Progress"
                dataOnboarding="nav-progress"
              />
              <button
                type="button"
                onClick={goToEmpathy}
                aria-label="Empathy Queue"
                aria-current={isEmpathyActive ? 'page' : undefined}
                data-onboarding="nav-empathy"
                className={`ee-topbar__link ${isEmpathyActive ? 'ee-topbar__link--active' : ''} ${isTourIntro && tourSection !== 'empathy' ? 'opacity-30' : ''} ${isTourIntro && tourSection === 'empathy' ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''}`}
              >
                <span className="ee-topbar__e" aria-hidden="true">
                  E
                </span>
                <span>Empathy</span>
              </button>
              <DesktopLinkButton
                to="/app/games"
                icon="games"
                label="Games"
                dataOnboarding="nav-games"
              />
              <DesktopLinkButton
                to="/app/tools"
                icon="tools"
                label="Tools"
                dataOnboarding="nav-tools"
              />
            </div>

            {/* Profile block - links to profile */}
            <Link
              to="/app/profile"
              onClick={handleProfileClick}
              data-testid="desktop-profile-link"
              data-onboarding="nav-profile"
              className={`ee-profile ${isTourIntro && tourSection !== 'profile' ? 'opacity-30' : ''} ${isProfileTourTarget ? 'rounded-full animate-pulse' : ''}`}
              style={profileTourStyle}
            >
              {/* alt="": the first name beside it is the link's text, and a
                  full name in the alt made the link announce the name twice
                  (#1022 review). The phone avatar below is its link's only
                  content and keeps the name. */}
              <ProfileAvatar size={40} alt="" paid={isPaidPlan} />
              <span className="ee-profile__text">
                <span className="ee-profile__name">{firstName}</span>
                {/* A space for the accessible name ("Ada View profile", not
                    "AdaView profile"); a flex column does not render it. */}{' '}
                <span className="ee-profile__sub">View profile</span>
              </span>
              <LuChevronRight className="ee-profile__chevron" aria-hidden="true" />
            </Link>
          </div>
        </nav>
      ) : null}

      {/* Compact header - below 1280px (phones and tablets): logo, the Empy
          and support launchers, the profile avatar. In the FLOW, never fixed
          or absolute, so the page starts below it and cannot run under any
          of them. Redesign PR3 (2026-10-20), two overlaps it removes: the
          avatar used to be `absolute` at the page's top right from 768 to
          1279 (over the right end of the Circles / 1-on-1 control), and the
          launchers floated at right 16px (over the right end of every card).
          Hidden, not unmounted, under a full-screen stage: the support
          launcher renders the open chat, which a call starting must not
          throw away. No z-index, transform or filter on it (see shell.css). */}
      {!isDesktop && (
        <header
          data-testid="mobile-shell-header"
          data-notice-anchor=""
          className={`ee-shell ee-mhead ${fullScreenStageOpen ? 'hidden' : ''}`}
        >
          <div className="ee-mhead__inner">
            <Link to={brandTo} className="ee-brand">
              <BrandLogo />
              <span className="ee-brand__word">EmpathEase</span>
            </Link>
            <div className="ee-mhead__actions">
              {/* Empy AI + support chat: both are authed AI surfaces, so guests
                  don't get them (Empy navigates to /app/empy/*, which guests can't use). */}
              {!isGuest ? (
                <>
                  <EmpyFloatingButton position="docked" />
                  <SupportChatButton position="docked" />
                </>
              ) : null}
              <Link
                to="/app/profile"
                onClick={handleProfileClick}
                data-testid="mobile-profile-link"
                data-onboarding="nav-profile"
                className={`ee-mhead__profile ${isProfileTourTarget ? 'relative z-[201] animate-pulse' : ''} ${isTourIntro && tourSection !== 'profile' ? 'opacity-30' : ''}`}
                style={profileTourStyle}
              >
                <ProfileAvatar size={40} alt={user?.name || 'Profile'} paid={isPaidPlan} />
              </Link>
            </div>
          </div>
        </header>
      )}

      {/* Main Content Area with safe area top padding and bottom spacing for nav test test */}
      {/* `hidden`, never unmounted: the full-screen stage is rendered BY this
          child tree, so dropping `children` would unmount the stage, which
          would clear the flag, which would bring the chrome back, which would
          remount the stage. `display:none` also takes the page's height out of
          the document, which is what stops the page scrolling under the stage
          — locking `body`'s overflow does not, because this app's `html` has
          an overflow of its own and body's never propagates past it. */}
      <main
        className={`${isDesktop ? 'pt-20 pb-8' : 'pb-24'} ${fullScreenStageOpen ? 'hidden' : ''}`}
        style={{
          minHeight: `calc(100vh - ${safeAreaValues.top}px - ${safeAreaValues.bottom}px)`,
          background: `${background}`,
        }}
      >
        {/* Status banners live HERE (inside <main>, below the fixed header) — rendered
            from ProtectedRoute they sit under the fixed navbar and are invisible. */}
        {isGuest ? (
          <GuestBanner />
        ) : user && user.verification_required === false && user.email_verified === false ? (
          <VerifyEmailBanner />
        ) : null}
        {/* Desktop content container for max-width */}
        <div className={`relative ${isDesktop ? 'max-w-6xl mx-auto px-6' : ''}`}>
          {children}
          {/* Spacer so content can scroll past the floating Empy button */}
          <div className="h-24" />
        </div>
      </main>

      {/* Enhanced Fixed Navigation Bar with Venmo-style cutout and special Empathy button */}
      {/* Mobile only - hidden on desktop */}
      {!keyboardVisible && !isDesktop && !fullScreenStageOpen && (
        <>
          {/* Empathy Button - Positioned to share same center as cutout circle */}
          <div
            // It rises ~32px above the nav's top: utils/keyboard.ts keeps
            // focused-form controls above it too (fix round A1).
            data-fixed-bottom
            className={`ee-shell fixed bottom-0 left-1/2 transform -translate-x-1/2 ${isTourIntro ? 'z-[201]' : 'z-(--ee-z-nav-raised)'} ${isTourIntro && tourSection !== 'empathy' ? 'opacity-30' : ''}`}
            style={{
              bottom: `calc(27px + max(env(safe-area-inset-bottom), calc(var(--spacing) * 2)))`, // Navbar is 66px tall, button center should be at top edge (66px from bottom), button is 64px tall so bottom = 66-32 = 34, but adjust -1px for visual alignment
            }}
          >
            <button
              type="button"
              onClick={goToEmpathy}
              data-onboarding="empathy-e-button"
              className="ee-bnav__e group relative"
              aria-label="Empathy Queue"
              aria-current={isEmpathyActive ? 'page' : undefined}
            >
              {/* Background circle matching the cutout's border */}
              <div className="ee-bnav__e-ring absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[74px] h-[74px] rounded-full -z-10"></div>

              {/* The orange face (shell.css: the button orange, so the white E holds 4.5:1) */}
              <div className="ee-bnav__e-face relative w-16 h-16 rounded-full flex items-center justify-center transform transition-all duration-200 group-hover:scale-105 group-active:scale-95">
                <span className="text-2xl font-bold text-(--ee-on-accent)">E</span>
              </div>
            </button>
          </div>

          {/* Navbar with semicircular cutout matching button */}
          <nav
            // utils/keyboard.ts keeps focused-form controls above this bar.
            data-fixed-bottom
            className={`ee-shell fixed bottom-0 left-0 right-0 ${navZClass} safe-area-horizontal`}
            style={{
              bottom: `0px`,
              left: `${safeAreaValues.left}px`,
              right: `${safeAreaValues.right}px`,
            }}
          >
            <div className="relative">
              {/* Navbar background with cutout */}
              <div
                className="ee-bnav__surface absolute top-0 left-0 w-full"
                style={{
                  height: 'calc(66px + max(env(safe-area-inset-bottom), calc(var(--spacing) * 2)))',
                  // Create cutout using radial gradient
                  maskImage: 'radial-gradient(circle at 50% 0, transparent 43px, black 44px)',
                  WebkitMaskImage:
                    '-webkit-radial-gradient(center top, circle, transparent 43px, black 44px)',
                  WebkitMaskSize: '100% 100%',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center top',
                }}
              ></div>

              {/* Border line at top of navbar */}
              <div
                className="absolute top-0 left-0 right-0 h-px pointer-events-none"
                style={{ zIndex: 1 }}
              >
                {/* Left border line */}
                <div
                  className="ee-bnav__rule absolute left-0 top-0 h-px"
                  style={{ width: 'calc(50% - 44px)' }}
                ></div>
                {/* Right border line */}
                <div
                  className="ee-bnav__rule absolute right-0 top-0 h-px"
                  style={{ width: 'calc(50% - 44px)' }}
                ></div>

                {/* Curved border for the cutout */}
                <div
                  className="absolute top-0 left-1/2 transform -translate-x-1/2"
                  style={{
                    width: '88px',
                    height: '44px',
                    borderBottom: '1px solid var(--ee-border-strong)',
                    borderLeft: '1px solid var(--ee-border-strong)',
                    borderRight: '1px solid var(--ee-border-strong)',
                    borderBottomLeftRadius: '44px',
                    borderBottomRightRadius: '44px',
                    pointerEvents: 'none',
                  }}
                ></div>
              </div>

              {/* Navbar content */}
              <div
                className="relative z-10"
                style={{
                  height: 'calc(66px + max(env(safe-area-inset-bottom), calc(var(--spacing) * 2)))',
                  paddingBottom: `max(env(safe-area-inset-bottom), calc(var(--spacing) * 2))`,
                  paddingTop: '4px',
                }}
              >
                <div
                  className="px-2"
                  style={{
                    height: '66px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <div className="relative flex items-center justify-around max-w-xl mx-auto w-full">
                    {/* Left Section */}
                    <div className="flex gap-1 flex-1 justify-around">
                      <LinkButton
                        to="/app"
                        icon="course"
                        label="Course"
                        dataOnboarding="nav-course"
                      />
                      <LinkButton
                        to="/app/progress"
                        icon="progress"
                        label="Progress"
                        dataOnboarding="nav-progress"
                      />
                    </div>

                    {/* Center - Empathy Label - positioned at bottom, transparent to clicks except the text */}
                    {/* `self-stretch`, not `height: 100%` (a percentage of the
                        row's auto height is auto, which centred the label
                        behind the E): the column is as tall as the items
                        beside it, so its label shares their baseline. */}
                    <div
                      className="flex-shrink-0 self-stretch flex flex-col items-center justify-end px-1 pb-1 pointer-events-none"
                      data-onboarding="nav-empathy"
                      style={{ width: '88px' }}
                    >
                      {/* The label under the raised E (the approved mockup).
                          The E above is the control screen readers and the
                          keyboard use ("Empathy Queue"); this is the same
                          action for a thumb that lands on the word. */}
                      <button
                        type="button"
                        onClick={goToEmpathy}
                        tabIndex={-1}
                        aria-hidden="true"
                        className={`ee-bnav__e-label pointer-events-auto ${isEmpathyActive ? 'ee-bnav__e-label--active' : ''}`}
                      >
                        Empathy
                      </button>
                    </div>

                    {/* Right Section */}
                    <div className="flex gap-1 flex-1 justify-around">
                      <LinkButton
                        to="/app/games"
                        icon="games"
                        label="Games"
                        dataOnboarding="nav-games"
                      />
                      <LinkButton
                        to="/app/tools"
                        icon="tools"
                        label="Tools"
                        dataOnboarding="nav-tools"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </>
      )}

      {/* Global Queue Overlay - Persists across all dashboard pages */}
      <QueueOverlay
        mode={giverState !== 'OFFLINE' ? 'giver' : 'seeker'}
        giverState={giverState}
        seekerState={seekerState}
        queuePosition={queuePosition}
        estimatedWaitMinutes={estimatedWaitMinutes}
        reliabilityScore={reliabilityScore}
        availableGiversCount={queueStats?.givers_in_waiting_room}
        onLeave={fireAndForget(handleLeaveQueue)}
        isLeaving={isLeavingQueue}
      />

      {/* Desktop: the Empy AI + support chat launchers float at the bottom
          right (below 1280 they are docked in the compact header above). Both
          are authed AI surfaces, so guests don't get them (Empy navigates to
          /app/empy/*, which guests can't use). */}
      {isDesktop && !isGuest ? (
        <div className={fullScreenStageOpen ? 'hidden' : 'contents'}>
          <EmpyFloatingButton />
          <SupportChatButton />
        </div>
      ) : null}
    </div>
  );
}

export default DashboardLayout;
